import { extractContentRefs } from '@gitbook/doc-core';
import { useHiveBranch } from '@gitbook/hive-react';
import type { Change, Document } from '@gitbook/slate';
import {
    decorateClipboardEvent,
    type EditorContainer,
    type Plugin,
    removeCutContent,
} from '@gitbook/slate-react';
import { useStableAPI } from '@gitbook/util-react-hooks';

import type { ContentEditorContextValue } from '../contexts';
import { GITBOOK_CONTEXT_CONTENT_TYPE, GITBOOK_FILES_CONTENT_TYPE } from '../types';

/**
 * Slate plugin to handle copying by the user.
 */
export function useCopyPlugin(editorContext: ContentEditorContextValue): Plugin {
    const hiveBranch = useHiveBranch();

    const getTransferData = (document: Document): { [contentType: string]: string } => {
        const result: { [contentType: string]: string } = {};

        if (hiveBranch) {
            const refs = extractContentRefs(document);
            const files = {};
            for (const ref of Object.values(refs).flat()) {
                if (!ref || ref.kind !== 'file') {
                    continue;
                }

                const fileId = ref.file;
                const file = hiveBranch.revision.files[fileId];

                if (!file) {
                    continue;
                }

                files[fileId] = file;
            }

            if (Object.keys(files).length) {
                result[GITBOOK_FILES_CONTENT_TYPE] = JSON.stringify(files);
            }
        }

        result[GITBOOK_CONTEXT_CONTENT_TYPE] = JSON.stringify(editorContext.copyPaste.getContext());

        return result;
    };

    return useStableAPI({
        onCut: (event: ClipboardEvent, change: Change, editor: EditorContainer) => {
            decorateClipboardEvent(event, change.value, {
                getTransferData,
            });

            removeCutContent(event, change.value, editor);
            return true;
        },
        onCopy: (event: ClipboardEvent, change: Change) => {
            decorateClipboardEvent(event, change.value, {
                getTransferData,
            });

            return true;
        },
    });
}
